import { useState } from 'react';
import { Alert, Button, Modal, Skeleton, Space, Typography } from 'antd';

import { previewNotify } from '../../services/automations';

/**
 * "Show me what this actually says" — a Preview button plus the modal it opens,
 * shared by both rule editors.
 *
 * The request is built from the CURRENT UNSAVED draft (the caller hands us a
 * `buildRequest` thunk, not a value, so the payload is read at click time and
 * never goes stale behind a memo). That is the feature: a manager should not
 * have to save a rule to find out whether the title renders "Suzy has no MAR
 * today" or "has no  today".
 *
 * The endpoint runs the SAME validation the save runs, so a 400 here is
 * field-level authoring feedback and is shown verbatim rather than paraphrased
 * — it is the only text that says which field is wrong.
 *
 * Two things are deliberately faithful rather than pretty:
 *
 * - CTA buttons render DISABLED. They are a picture of the notification, not
 *   the notification; a live button here would start an item the manager never
 *   asked for.
 * - The email is rendered in a `sandbox=""` iframe. It is server-generated
 *   HTML, but it is still a document being injected into the app's own origin,
 *   and an empty sandbox attribute denies everything — scripts, forms,
 *   same-origin, navigation. `srcDoc` keeps it a document rather than a string
 *   of markup we would otherwise have to trust enough to inject.
 */

const { Text, Paragraph } = Typography;

/** "Casey Care, Robin Read and 3 more" — the count is the API's, not the slice's. */
function Recipients( { recipients = [], recipientCount = 0 } ) {
	if ( ! recipients.length ) {
		return <Text type='secondary'>Will reach: nobody — no one in this audience can see these items.</Text>;
	}
	const names = recipients.map( person => person?.name ).filter( Boolean ).join( ', ' );
	const extra = Math.max( recipientCount - recipients.length, 0 );
	return (
		<Text type='secondary'>
			{`Will reach: ${ names }`}
			{extra ? ` and ${ extra } more` : ''}
		</Text>
	);
}

export default function NotifyPreviewButton( {
	collectionId,
	buildRequest,
	disabled = false,
	label = 'Preview',
} ) {

	const [ open, setOpen ] = useState( false );
	const [ loading, setLoading ] = useState( false );
	const [ preview, setPreview ] = useState( null );
	const [ error, setError ] = useState( null );

	const handleClick = async () => {
		setOpen( true );
		setLoading( true );
		setPreview( null );
		setError( null );
		try {
			const data = await previewNotify( collectionId, buildRequest() );
			setPreview( data || null );
		}
		catch ( err ) {
			// Verbatim: this endpoint's 400s are the save's 400s.
			setError( err?.response?.data?.message || 'Could not build a preview.' );
		}
		finally {
			setLoading( false );
		}
	};

	const warnings = Array.isArray( preview?.warnings ) ? preview.warnings : [];

	return (<>
		<Button disabled={disabled} onClick={handleClick}>{label}</Button>

		<Modal
			open={open}
			title='Notification preview'
			width={720}
			onCancel={() => setOpen( false )}
			footer={<Button onClick={() => setOpen( false )}>Close</Button>}
			destroyOnClose
		>
			{loading ? <Skeleton active paragraph={{ rows: 4 }} /> : null}

			{! loading && error ? (
				<Alert type='error' showIcon message='This notification cannot be sent yet' description={error} />
			) : null}

			{! loading && ! error && preview ? (
				<Space direction='vertical' size={16} style={{ width: '100%' }}>

					{warnings.map( warning => (
						<Alert key={warning} type='warning' showIcon message={warning} />
					) )}

					<section className='notify-preview__in-app' aria-label='In-app notification preview'>
						<Text strong className='d-block'>In-app</Text>
						<div className='notify-preview__card'>
							<Text strong className='d-block'>{preview.title}</Text>
							{preview.body ? <Paragraph style={{ marginBottom: 8 }}>{preview.body}</Paragraph> : null}
							<Space wrap>
								{( preview.ctas || [] ).map( ( cta, index ) => (
									// Disabled on purpose: a preview must not be able to act.
									<Button key={`${ cta?.label }-${ index }`} size='small' disabled>
										{cta?.label}
									</Button>
								) )}
							</Space>
						</div>
						<Recipients recipients={preview.recipients} recipientCount={preview.recipientCount} />
					</section>

					{preview.email ? (
						<section className='notify-preview__email' aria-label='Email preview'>
							<Text strong className='d-block'>Email</Text>
							<Text type='secondary' className='d-block'>{`Subject: ${ preview.email.subject || '' }`}</Text>
							<iframe
								title='Email preview'
								className='notify-preview__frame'
								sandbox=''
								srcDoc={preview.email.html || ''}
								style={{ width: '100%', height: 320, border: '1px solid rgba(0,0,0,0.12)', borderRadius: 6 }}
							/>
						</section>
					) : null}

				</Space>
			) : null}
		</Modal>
	</>);
}
